// Portadas dibujadas (SVG) de las fichas del lobby «En vivo» (estilo galería de juegos).
// Todo es vectorial: tablero XIMA en perspectiva, bloques apilados, dados, ciencia, versus...

let uid = 0;
const nextId = (p) => `${p}${(uid += 1).toString(36)}`;

export const TONES = {
  science: { a: "#1b4fd8", b: "#0a1d5c", glow: "#5aa8ff", ink: "#bcd8ff" },
  cienzar: { a: "#14a05a", b: "#063a24", glow: "#5dffa0", ink: "#c6ffdd" },
  contrazar: { a: "#d8323c", b: "#4a0a17", glow: "#ff7a7f", ink: "#ffd0d2" },
  chance: { a: "#f08a1c", b: "#5a2405", glow: "#ffc861", ink: "#ffe6bd" },
  gold: { a: "#c98a12", b: "#3a2203", glow: "#ffd76a", ink: "#fff0c2" },
  purple: { a: "#7c3aed", b: "#230b4f", glow: "#c69bff", ink: "#ead9ff" },
  teal: { a: "#0e9fa8", b: "#06343c", glow: "#5ff3ff", ink: "#c9fbff" },
  pink: { a: "#d9368f", b: "#4a0a2e", glow: "#ff8fcb", ink: "#ffd3ea" },
  slate: { a: "#3a4a74", b: "#0d1430", glow: "#93a6d8", ink: "#dbe4ff" },
};

export const BLOCK = {
  blue: ["#5b8cff", "#2f63e8", "#1c3fa6"],
  red: ["#ff6b6b", "#e23b3b", "#9f1f22"],
  green: ["#5ee08a", "#22b25a", "#13703a"],
  yellow: ["#ffe066", "#f2bf1d", "#a77f06"],
  white: ["#ffffff", "#d9dee9", "#9aa3b8"],
  black: ["#4a5064", "#262a38", "#12141c"],
  gold: ["#ffe9a3", "#f5b531", "#a8700c"],
  silver: ["#f3f6fb", "#c3cad8", "#7c869c"],
  bronze: ["#f4b38a", "#c97a45", "#7f4320"],
};

const PLAYER_COLORS = ["blue", "red", "green", "yellow"];

// ---------- piezas básicas ----------

const cube = (x, y, s, colors, extra = "") => {
  // x,y = vértice superior del rombo de arriba; s = ancho del cubo
  const [top, left, right] = colors;
  const h = s / 2;
  const d = s * 0.62;
  return `<g ${extra}>
    <path d="M${x} ${y} L${x + h} ${y + h / 2} L${x} ${y + h} L${x - h} ${y + h / 2}Z" fill="${top}"/>
    <path d="M${x - h} ${y + h / 2} L${x} ${y + h} L${x} ${y + h + d} L${x - h} ${y + h / 2 + d}Z" fill="${left}"/>
    <path d="M${x + h} ${y + h / 2} L${x} ${y + h} L${x} ${y + h + d} L${x + h} ${y + h / 2 + d}Z" fill="${right}"/>
    <path d="M${x} ${y} L${x + h} ${y + h / 2} L${x} ${y + h} L${x - h} ${y + h / 2}Z" fill="none" stroke="rgba(255,255,255,.35)" stroke-width="${s * 0.03}"/>
  </g>`;
};

const star = (cx, cy, r, fill = "#ffd23f") => {
  const pts = [];
  for (let i = 0; i < 10; i += 1) {
    const rad = i % 2 ? r * 0.45 : r;
    const ang = -Math.PI / 2 + (i * Math.PI) / 5;
    pts.push(`${(cx + rad * Math.cos(ang)).toFixed(1)},${(cy + rad * Math.sin(ang)).toFixed(1)}`);
  }
  return `<polygon points="${pts.join(" ")}" fill="${fill}" stroke="#7a4b00" stroke-width="${r * 0.12}" stroke-linejoin="round"/>`;
};

// Tablero XIMA en perspectiva isométrica con anillos (borde, verde agua, morado, cima).
// stacks: [{i,j,colors:[...]}] de abajo arriba; special: {i,j,h}
export const isoBoard = ({ cx = 160, cy = 70, cell = 14, n = 13, stacks = [], special = null, glow = "#5aa8ff", flat = 0.5 } = {}) => {
  const half = (n - 1) / 2;
  const tile = (i, j) => {
    const x = cx + (i - j) * (cell / 2);
    const y = cy + (i + j) * (cell * flat) / 2;
    return [x, y];
  };
  const ringColor = (i, j) => {
    const d = Math.max(Math.abs(i - half), Math.abs(j - half));
    if (d === 0) return ["#ffcf4a", "#d99a12"];
    if (d >= half) return ["#16244f", "#0f1a3c"];
    if (d <= 2) return ["#7a52e6", "#6340c9"];
    return ["#1aa79a", "#14897e"];
  };
  const thick = cell * 0.45;
  const [lx, ly] = tile(0, n);
  const [rx, ry] = tile(n, 0);
  const [bx, by] = tile(n, n);
  const [tx, ty] = tile(0, 0);
  let out = `<g>
    <ellipse cx="${cx}" cy="${by + thick + 6}" rx="${(rx - lx) * 0.55}" ry="${cell * 1.6}" fill="rgba(0,0,0,.45)" filter="blur(6px)"/>
    <path d="M${lx} ${ly} L${bx} ${by} L${bx} ${by + thick} L${lx} ${ly + thick}Z" fill="#0b1433"/>
    <path d="M${rx} ${ry} L${bx} ${by} L${bx} ${by + thick} L${rx} ${ry + thick}Z" fill="#070d24"/>
    <path d="M${tx} ${ty} L${rx} ${ry} L${bx} ${by} L${lx} ${ly}Z" fill="#0c1636" stroke="${glow}" stroke-opacity=".55" stroke-width="1.2"/>`;
  for (let i = 0; i < n; i += 1) {
    for (let j = 0; j < n; j += 1) {
      const [x, y] = tile(i, j);
      const [c1, c2] = ringColor(i, j);
      const hw = cell / 2;
      const hh = (cell * flat) / 2;
      out += `<path d="M${x} ${y + 0.6} L${x + hw - 0.6} ${y + hh} L${x} ${y + 2 * hh - 0.6} L${x - hw + 0.6} ${y + hh}Z" fill="${(i + j) % 2 ? c1 : c2}"/>`;
    }
  }
  out += "</g>";
  const pieces = [];
  stacks.forEach((st) => {
    st.colors.forEach((c, k) => pieces.push({ i: st.i, j: st.j, k, colors: BLOCK[c] || c }));
  });
  if (special) pieces.push({ i: special.i, j: special.j, k: special.h || 0, special: true });
  pieces.sort((a, b) => a.i + a.j - (b.i + b.j) || a.k - b.k);
  const s = cell * 0.92;
  pieces.forEach((p) => {
    const [x, y] = tile(p.i, p.j);
    const top = y + (cell * flat) / 2 - s / 2 + cell * 0.04 - p.k * s * 0.62;
    if (p.special) {
      out += cube(x, top, s, BLOCK.white);
      out += star(x, top + s / 2, s * 0.32);
    } else out += cube(x, top, s, p.colors);
  });
  return out;
};

// Dado en perspectiva con puntos.
export const die = (x, y, s, value = 5, rot = 0, body = ["#ffffff", "#e3e6ee", "#b9bfd0"], pip = "#16161d") => {
  const h = s / 2;
  const pipsTop = {
    1: [[0, 0]],
    2: [[-0.5, -0.5], [0.5, 0.5]],
    3: [[-0.5, -0.5], [0, 0], [0.5, 0.5]],
    4: [[-0.5, -0.5], [0.5, -0.5], [-0.5, 0.5], [0.5, 0.5]],
    5: [[-0.5, -0.5], [0.5, -0.5], [0, 0], [-0.5, 0.5], [0.5, 0.5]],
    6: [[-0.5, -0.55], [0.5, -0.55], [-0.5, 0], [0.5, 0], [-0.5, 0.55], [0.5, 0.55]],
  };
  const r = s * 0.075;
  // cara de arriba: rombo centrado en (x, y+h/2)
  const topPts = (pipsTop[value] || pipsTop[5])
    .map(([u, v]) => {
      const px = x + (u - v) * (h * 0.5);
      const py = y + h / 2 + (u + v) * (h * 0.25);
      return `<ellipse cx="${px}" cy="${py}" rx="${r}" ry="${r * 0.55}" fill="${pip}"/>`;
    })
    .join("");
  const d = s * 0.78;
  const side = (oy, sign, vals) =>
    vals
      .map(([u, v]) => {
        const px = x + sign * (h * 0.5) + u * h * 0.32 * sign;
        const py = y + h + d / 2 + v * d * 0.28 + -sign * u * h * 0.16 * sign;
        return `<ellipse cx="${px}" cy="${py - (sign > 0 ? h * 0.25 : h * 0.25) + oy}" rx="${r * 0.8}" ry="${r}" fill="${pip}"/>`;
      })
      .join("");
  return `<g transform="rotate(${rot} ${x} ${y + s / 2})">
    <ellipse cx="${x}" cy="${y + h + d + 4}" rx="${s * 0.62}" ry="${s * 0.16}" fill="rgba(0,0,0,.35)"/>
    <path d="M${x} ${y} L${x + h} ${y + h / 2} L${x} ${y + h} L${x - h} ${y + h / 2}Z" fill="${body[0]}" stroke="${body[2]}" stroke-width="${s * 0.02}" stroke-linejoin="round"/>
    <path d="M${x - h} ${y + h / 2} L${x} ${y + h} L${x} ${y + h + d} L${x - h} ${y + h / 2 + d}Z" fill="${body[1]}" stroke="${body[2]}" stroke-width="${s * 0.02}" stroke-linejoin="round"/>
    <path d="M${x + h} ${y + h / 2} L${x} ${y + h} L${x} ${y + h + d} L${x + h} ${y + h / 2 + d}Z" fill="${body[2]}" stroke="${body[2]}" stroke-width="${s * 0.02}" stroke-linejoin="round"/>
    ${topPts}
    ${side(0, -1, [[0, -0.2], [0, 0.45]])}
    ${side(0, 1, [[-0.4, -0.35], [0, 0.05], [0.4, 0.45]])}
  </g>`;
};

export const atom = (cx, cy, r, color = "#8fd0ff", width = 2.4) => `<g fill="none" stroke="${color}" stroke-width="${width}" opacity=".95">
  <ellipse cx="${cx}" cy="${cy}" rx="${r}" ry="${r * 0.36}"/>
  <ellipse cx="${cx}" cy="${cy}" rx="${r}" ry="${r * 0.36}" transform="rotate(60 ${cx} ${cy})"/>
  <ellipse cx="${cx}" cy="${cy}" rx="${r}" ry="${r * 0.36}" transform="rotate(-60 ${cx} ${cy})"/>
  <circle cx="${cx}" cy="${cy}" r="${r * 0.14}" fill="${color}"/>
  <circle cx="${cx + r}" cy="${cy}" r="${r * 0.07}" fill="#fff" stroke="none"/>
</g>`;

const flask = (x, y, s, liquid = "#5dffa0") => `<g transform="translate(${x} ${y}) scale(${s / 100})">
  <path d="M38 6h24v6h-4v26l28 46c4 8-1 14-10 14H24c-9 0-14-6-10-14l28-46V12h-4z" fill="rgba(255,255,255,.14)" stroke="#e8f4ff" stroke-width="4" stroke-linejoin="round"/>
  <path d="M27 64h46l11 18c3 5 0 9-6 9H22c-6 0-9-4-6-9z" fill="${liquid}" opacity=".9"/>
  <circle cx="40" cy="76" r="4" fill="#fff" opacity=".7"/><circle cx="56" cy="70" r="3" fill="#fff" opacity=".6"/><circle cx="62" cy="82" r="2.5" fill="#fff" opacity=".6"/>
</g>`;

const person = (x, y, s, color) => `<g transform="translate(${x} ${y}) scale(${s / 100})">
  <circle cx="50" cy="30" r="22" fill="${color}" stroke="#fff" stroke-width="5"/>
  <path d="M10 98c2-26 18-40 40-40s38 14 40 40z" fill="${color}" stroke="#fff" stroke-width="5" stroke-linejoin="round"/>
</g>`;

const vsBadge = (cx, cy, s) => `<g transform="translate(${cx} ${cy})">
  <path d="M${-s * 0.2} ${-s * 0.62} L${s * 0.32} ${-s * 0.62} L${s * 0.04} ${-s * 0.06} L${s * 0.3} ${-s * 0.06} L${-s * 0.3} ${s * 0.7} L${-s * 0.08} ${s * 0.08} L${-s * 0.34} ${s * 0.08}Z" fill="#ffd23f" stroke="#7a4b00" stroke-width="${s * 0.05}" stroke-linejoin="round"/>
  <text x="0" y="${s * 0.2}" text-anchor="middle" font-family="'Baloo 2','Arial Black',sans-serif" font-weight="900" font-size="${s * 0.62}" fill="#fff" stroke="#1a0f3a" stroke-width="${s * 0.07}" paint-order="stroke" font-style="italic">VS</text>
</g>`;

const sparkles = (count, color, seed = 1, w = 320, h = 200) => {
  let a = seed * 9301 + 49297;
  const rnd = () => {
    a = (a * 9301 + 49297) % 233280;
    return a / 233280;
  };
  let out = "";
  for (let i = 0; i < count; i += 1) {
    const x = rnd() * w;
    const y = rnd() * h;
    const r = 0.6 + rnd() * 1.8;
    out += `<circle cx="${x.toFixed(1)}" cy="${y.toFixed(1)}" r="${r.toFixed(1)}" fill="${color}" opacity="${(0.25 + rnd() * 0.6).toFixed(2)}"/>`;
  }
  return out;
};

// Fondo con degradado, rayos de luz y motivos del modo.
const backdrop = (tone, motif, seed = 1) => {
  const t = TONES[tone] || TONES.slate;
  const g = nextId("g");
  const r = nextId("r");
  let pattern = "";
  if (motif === "science") {
    pattern = `<g stroke="${t.ink}" stroke-opacity=".16" fill="none" stroke-width="1.4">
      ${[0, 1, 2, 3, 4, 5, 6].map((k) => `<path d="M${k * 52 - 10} 20 l15 -9 l15 9 v17 l-15 9 l-15 -9z"/><path d="M${k * 52 + 16} 150 l15 -9 l15 9 v17 l-15 9 l-15 -9z"/>`).join("")}
    </g>
    <g fill="${t.ink}" fill-opacity=".16" font-family="Georgia,serif" font-style="italic" font-size="13">
      <text x="14" y="70">E=mc²</text><text x="250" y="60">∑ x·y</text><text x="230" y="190">√13</text><text x="20" y="182">π</text>
    </g>`;
  } else if (motif === "dice") {
    pattern = `<g fill="${t.ink}" fill-opacity=".14">
      ${[[20, 30], [280, 40], [40, 170], [270, 165], [150, 18]].map(([x, y]) => `<rect x="${x - 11}" y="${y - 11}" width="22" height="22" rx="5" transform="rotate(20 ${x} ${y})"/>`).join("")}
    </g>${sparkles(26, t.glow, seed)}`;
  } else if (motif === "mix") {
    pattern = `<g stroke="${t.ink}" stroke-opacity=".15" fill="none" stroke-width="1.3">
      ${[0, 1, 2, 3].map((k) => `<path d="M${k * 80 + 6} 26 l13 -8 l13 8 v15 l-13 8 l-13 -8z"/>`).join("")}
    </g>
    <g fill="${t.ink}" fill-opacity=".14">${[[40, 170], [280, 160], [300, 30]].map(([x, y]) => `<rect x="${x - 10}" y="${y - 10}" width="20" height="20" rx="5" transform="rotate(25 ${x} ${y})"/>`).join("")}</g>`;
  } else {
    pattern = sparkles(30, t.glow, seed);
  }
  return `<defs>
      <linearGradient id="${g}" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stop-color="${t.a}"/><stop offset="1" stop-color="${t.b}"/></linearGradient>
      <radialGradient id="${r}" cx=".5" cy=".42" r=".6"><stop offset="0" stop-color="${t.glow}" stop-opacity=".55"/><stop offset="1" stop-color="${t.glow}" stop-opacity="0"/></radialGradient>
    </defs>
    <rect width="320" height="200" fill="url(#${g})"/>
    <g opacity=".22" fill="#fff">${[0, 1, 2, 3, 4, 5, 6, 7].map((k) => `<path d="M160 90 L${k * 46 - 20} -10 L${k * 46 + 2} -10Z"/>`).join("")}</g>
    <rect width="320" height="200" fill="url(#${r})"/>
    ${pattern}`;
};

const wrap = (inner) =>
  `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 200" preserveAspectRatio="xMidYMid slice">${inner}</svg>`;

const label = (text, sub = "", y = 186) =>
  text
    ? `<text x="160" y="${y}" text-anchor="middle" font-family="'Baloo 2','Arial Black',sans-serif" font-weight="900" font-size="30" fill="#fff" stroke="#0a0f24" stroke-width="7" paint-order="stroke" letter-spacing="1">${text}</text>${
        sub ? `<text x="160" y="${y - 30}" text-anchor="middle" font-family="'Baloo 2',sans-serif" font-weight="800" font-size="13" fill="#fff" opacity=".9">${sub}</text>` : ""
      }`
    : "";

// Partida típica en el tablero según el número de jugadores.
const stacksFor = (players = 2, variant = 0) => {
  const colors = PLAYER_COLORS.slice(0, players);
  const base = [
    { i: 3, j: 5, colors: [colors[0]] },
    { i: 4, j: 8, colors: [colors[1], colors[0]] },
    { i: 5, j: 5, colors: [colors[0], colors[1], colors[0]] },
    { i: 7, j: 4, colors: [colors[1]] },
    { i: 8, j: 7, colors: [colors[0 + (players > 2 ? 2 : 0)], colors[1]] },
    { i: 6, j: 9, colors: [colors[1]] },
    { i: 9, j: 9, colors: [colors[players > 3 ? 3 : 0]] },
    { i: 2, j: 9, colors: [colors[players > 2 ? 2 : 1]] },
    { i: 10, j: 3, colors: [colors[players > 3 ? 3 : 1], colors[players > 3 ? 3 : 1]] },
    { i: 7, j: 7, colors: [colors[0], colors[1]] },
  ];
  if (players >= 3) base.push({ i: 9, j: 5, colors: [colors[2], colors[2], colors[0]] }, { i: 3, j: 2, colors: [colors[2]] });
  if (players >= 4) base.push({ i: 11, j: 8, colors: [colors[3]] }, { i: 5, j: 11, colors: [colors[3], colors[2]] });
  return base.slice(variant % 2, base.length);
};

// ---------- portadas ----------

// Modo principal grande (Ciencia, Cienzar, Contrazar, Azar).
export const modeHero = (mode, opts = {}) => {
  const { title = "", players = 2 } = opts;
  const tone = { ciencia: "science", cienzar: "cienzar", contrazar: "contrazar", azar: "chance" }[mode];
  const motif = { ciencia: "science", cienzar: "mix", contrazar: "mix", azar: "dice" }[mode];
  const t = TONES[tone];
  let extra = "";
  let boardY = 22;
  if (mode === "ciencia") {
    extra = `${atom(262, 46, 34, t.ink, 2.6)}${flask(16, 18, 54, "#5aa8ff")}`;
  } else if (mode === "azar") {
    extra = `${die(58, 88, 46, 5, -10)}${die(258, 80, 52, 6, 12, ["#ffe7b0", "#f5b531", "#a8700c"], "#3a1d00")}${die(230, 22, 28, 3, 20)}`;
  } else if (mode === "cienzar") {
    extra = `${atom(54, 54, 30, "#c6ffdd", 2.4)}${die(262, 84, 48, 4, 10)}${die(226, 30, 26, 2, -15, ["#c6ffdd", "#5ee08a", "#13703a"], "#06341b")}`;
  } else if (mode === "contrazar") {
    const cg = nextId("c");
    extra = `<defs><linearGradient id="${cg}" x1="0" x2="1"><stop offset="0" stop-color="#1b4fd8" stop-opacity=".75"/><stop offset=".5" stop-color="#000" stop-opacity="0"/><stop offset=".5" stop-color="#000" stop-opacity="0"/><stop offset="1" stop-color="#f08a1c" stop-opacity=".75"/></linearGradient></defs>
      <rect width="320" height="200" fill="url(#${cg})"/>
      ${atom(56, 56, 32, "#bcd8ff", 2.6)}${die(266, 70, 46, 6, 12, ["#ffe7b0", "#f5b531", "#a8700c"], "#3a1d00")}
      ${vsBadge(160, 34, 46)}`;
    boardY = 40;
  }
  const board = isoBoard({
    cx: 160,
    cy: boardY,
    cell: 19,
    stacks: stacksFor(players, 0),
    special: { i: 6, j: 6, h: 2 },
    glow: t.glow,
  });
  return wrap(`${backdrop(tone, motif, 3)}${board}${extra}${label(title)}`);
};

// Modo × número de jugadores (1 vs 1, 1 vs 2, 1 vs 3).
export const modePlayers = (mode, players = 2) => {
  const tone = { ciencia: "science", cienzar: "cienzar", contrazar: "contrazar", azar: "chance" }[mode];
  const motif = { ciencia: "science", cienzar: "mix", contrazar: "mix", azar: "dice" }[mode];
  const t = TONES[tone];
  const colors = PLAYER_COLORS.slice(0, players).map((c) => BLOCK[c][1]);
  const board = isoBoard({ cx: 160, cy: 40, cell: 16, stacks: stacksFor(players, 1), special: { i: 6, j: 6, h: 1 }, glow: t.glow });
  let people = "";
  if (players === 2) {
    people = `${person(18, 34, 58, colors[0])}${person(244, 34, 58, colors[1])}${vsBadge(160, 36, 40)}`;
  } else {
    people = person(14, 28, 50, colors[0]);
    colors.slice(1).forEach((c, k) => {
      people += person(248 + k * 22 - (players - 2) * 11, 26 + k * 8, 42, c);
    });
    people += vsBadge(160, 34, 34);
  }
  let deco = "";
  if (mode === "azar") deco = `${die(70, 128, 30, 5, -12)}${die(252, 126, 30, 2, 14)}`;
  if (mode === "cienzar") deco = `${atom(70, 146, 18, t.ink, 1.8)}${die(252, 126, 30, 4, 12)}`;
  if (mode === "contrazar") deco = `${atom(70, 146, 18, "#bcd8ff", 1.8)}${die(252, 126, 30, 6, 12, ["#ffe7b0", "#f5b531", "#a8700c"], "#3a1d00")}`;
  if (mode === "ciencia") deco = `${atom(70, 146, 18, t.ink, 1.8)}${atom(252, 146, 18, t.ink, 1.8)}`;
  const tag = `1 VS ${players - 1}`;
  return wrap(`${backdrop(tone, motif, players)}${board}${deco}${people}
    <g transform="translate(160 176)"><rect x="-54" y="-17" width="108" height="30" rx="15" fill="#0a0f24" stroke="${t.glow}" stroke-width="2"/><text y="5" text-anchor="middle" font-family="'Baloo 2','Arial Black',sans-serif" font-weight="900" font-size="19" fill="#fff">${tag}</text></g>`);
};

const puzzlePiece = (x, y, s, fill) => `<g transform="translate(${x} ${y}) scale(${s / 100})">
  <path d="M10 30h22c-4-14 4-24 16-24s20 10 16 24h22v22c14-4 24 4 24 16s-10 20-24 16v22H64c4 14-4 22-16 22s-20-8-16-22H10z" fill="${fill}" stroke="#fff" stroke-width="5" stroke-linejoin="round"/>
</g>`;

export const puzzleArt = (mode, soon = false) => {
  const tone = { ciencia: "science", cienzar: "cienzar", contrazar: "contrazar", azar: "chance" }[mode] || "purple";
  const t = TONES[tone];
  const board = isoBoard({ cx: 170, cy: 40, cell: 16, stacks: stacksFor(2, 0).slice(0, 7), special: { i: 6, j: 6, h: 1 }, glow: t.glow });
  let deco = "";
  if (mode === "azar") deco = die(262, 96, 34, 5, 12);
  if (mode === "cienzar") deco = `${atom(268, 50, 20, t.ink, 2)}${die(270, 104, 28, 3, 10)}`;
  if (mode === "contrazar") deco = `${atom(268, 50, 20, "#bcd8ff", 2)}${die(270, 104, 28, 6, 10, ["#ffe7b0", "#f5b531", "#a8700c"], "#3a1d00")}`;
  if (mode === "ciencia") deco = atom(266, 64, 26, t.ink, 2.2);
  const ribbon = soon
    ? `<g transform="rotate(-14 160 100)"><rect x="-20" y="84" width="360" height="34" fill="#0a0f24" opacity=".86"/><rect x="-20" y="84" width="360" height="34" fill="none" stroke="#ffd23f" stroke-width="2" stroke-dasharray="10 6"/><text x="160" y="108" text-anchor="middle" font-family="'Baloo 2','Arial Black',sans-serif" font-weight="900" font-size="20" fill="#ffd23f" letter-spacing="3">PRÓXIMAMENTE</text></g>`
    : "";
  return wrap(`${backdrop(tone, "dots", 7)}${board}${deco}${puzzlePiece(22, 70, 74, "#a855f7")}${ribbon}`);
};

const podium = (cx, by, s, motif) => {
  // Pódium hecho con columnas de bloques: oro (3), plata (2), bronce (1).
  let out = "";
  const cols = [
    { dx: -s * 1.05, h: 2, c: BLOCK.silver, n: "2" },
    { dx: s * 1.05, h: 1, c: BLOCK.bronze, n: "3" },
    { dx: 0, h: 3, c: BLOCK.gold, n: "1" },
  ];
  cols.forEach(({ dx, h, c, n }) => {
    for (let k = 0; k < h; k += 1) out += cube(cx + dx, by - k * s * 0.62 - s, s, c);
    const topY = by - (h - 1) * s * 0.62 - s;
    out += `<text x="${cx + dx}" y="${topY + s * 0.34}" text-anchor="middle" font-family="'Baloo 2','Arial Black',sans-serif" font-weight="900" font-size="${s * 0.34}" fill="#3a2200" transform="translate(0 0)">${n}</text>`;
  });
  out += `<g transform="translate(${cx - 22} ${by - 3 * s * 0.62 - s * 1.95})">${crown(44)}</g>`;
  return out + (motif || "");
};

const crown = (w) => `<path d="M2 ${w * 0.62} L${w * 0.08} ${w * 0.12} L${w * 0.3} ${w * 0.38} L${w * 0.5} 0 L${w * 0.7} ${w * 0.38} L${w * 0.92} ${w * 0.12} L${w - 2} ${w * 0.62}Z" fill="#ffd23f" stroke="#7a4b00" stroke-width="2.5" stroke-linejoin="round"/>`;

export const rankingArt = (mode) => {
  const tone = { ciencia: "science", cienzar: "cienzar", contrazar: "contrazar", azar: "chance", general: "gold" }[mode];
  const t = TONES[tone];
  let deco = "";
  if (mode === "azar") deco = `${die(40, 110, 34, 6, -10)}${die(282, 108, 34, 5, 10)}`;
  if (mode === "ciencia") deco = `${atom(44, 60, 26, t.ink)}${atom(278, 60, 26, t.ink)}`;
  if (mode === "cienzar") deco = `${atom(44, 60, 26, t.ink)}${die(282, 108, 34, 4, 10)}`;
  if (mode === "contrazar") deco = `${atom(44, 60, 26, "#bcd8ff")}${die(282, 108, 34, 6, 10, ["#ffe7b0", "#f5b531", "#a8700c"], "#3a1d00")}`;
  if (mode === "general") deco = `${star(44, 50, 18)}${star(282, 62, 14)}${star(260, 30, 9)}`;
  return wrap(`${backdrop(tone, mode === "general" ? "dots" : "dots", 11)}
    <ellipse cx="160" cy="176" rx="120" ry="16" fill="rgba(0,0,0,.35)"/>
    ${podium(160, 176, 46)}${deco}`);
};

const clock = (cx, cy, r, ring = "#fff") => `<g>
  <circle cx="${cx}" cy="${cy}" r="${r}" fill="#0a0f24" stroke="${ring}" stroke-width="${r * 0.12}"/>
  ${[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11].map((k) => {
    const a = (k * Math.PI) / 6;
    return `<line x1="${cx + Math.sin(a) * r * 0.72}" y1="${cy - Math.cos(a) * r * 0.72}" x2="${cx + Math.sin(a) * r * 0.84}" y2="${cy - Math.cos(a) * r * 0.84}" stroke="#fff" stroke-width="${r * 0.05}"/>`;
  }).join("")}
  <line x1="${cx}" y1="${cy}" x2="${cx}" y2="${cy - r * 0.55}" stroke="#ffd23f" stroke-width="${r * 0.1}" stroke-linecap="round"/>
  <line x1="${cx}" y1="${cy}" x2="${cx + r * 0.42}" y2="${cy + r * 0.12}" stroke="#fff" stroke-width="${r * 0.08}" stroke-linecap="round"/>
  <circle cx="${cx}" cy="${cy}" r="${r * 0.08}" fill="#ffd23f"/>
  <path d="M${cx - r * 1.05} ${cy - r * 0.35} a ${r * 1.1} ${r * 1.1} 0 0 1 ${r * 0.5} ${-r * 0.75}" fill="none" stroke="${ring}" stroke-width="${r * 0.1}" stroke-linecap="round"/>
  <path d="M${cx - r * 1.15} ${cy - r * 0.62} l${r * 0.12} ${r * 0.34} l${r * 0.3} ${-r * 0.18}" fill="none" stroke="${ring}" stroke-width="${r * 0.1}" stroke-linecap="round" stroke-linejoin="round"/>
</g>`;

const miniBoardCard = (x, y, w, tone, rot = 0) => {
  const t = TONES[tone];
  return `<g transform="rotate(${rot} ${x + w / 2} ${y + w * 0.35})">
    <rect x="${x}" y="${y}" width="${w}" height="${w * 0.7}" rx="8" fill="#0a1433" stroke="${t.glow}" stroke-width="2"/>
    ${isoBoard({ cx: x + w / 2, cy: y + 8, cell: w / 15, n: 13, stacks: stacksFor(2, 1).slice(0, 5), special: { i: 6, j: 6, h: 0 }, glow: t.glow })}
  </g>`;
};

export const historyArt = (mode) => {
  const tone = { ciencia: "science", cienzar: "cienzar", contrazar: "contrazar", azar: "chance" }[mode];
  const t = TONES[tone];
  let deco = "";
  if (mode === "azar") deco = die(286, 140, 28, 3, 10);
  if (mode === "cienzar") deco = die(286, 140, 28, 4, 10);
  if (mode === "contrazar") deco = die(286, 140, 28, 6, 10, ["#ffe7b0", "#f5b531", "#a8700c"], "#3a1d00");
  if (mode === "ciencia") deco = atom(288, 160, 18, t.ink, 1.8);
  return wrap(`${backdrop(tone, "dots", 5)}
    ${miniBoardCard(150, 30, 120, tone, 8)}${miniBoardCard(120, 56, 130, tone, -4)}
    ${clock(72, 100, 48, t.glow)}${deco}`);
};

// Iconos grandes para el resto de secciones.
const ICONS = {
  learnStart: (t) => `${isoBoard({ cx: 150, cy: 44, cell: 15, stacks: stacksFor(2, 0).slice(0, 4), special: { i: 6, j: 6, h: 0 }, glow: t.glow })}
    <g transform="translate(206 92)"><circle r="34" fill="#22c55e" stroke="#fff" stroke-width="5"/><path d="M-10 -16 L18 0 L-10 16Z" fill="#fff"/></g>`,
  videos: (t) => `<g transform="translate(70 34)"><rect width="180" height="118" rx="14" fill="#0a0f24" stroke="#fff" stroke-width="5"/>
    <rect x="10" y="10" width="160" height="98" rx="8" fill="${t.a}"/>
    ${isoBoard({ cx: 90, cy: 22, cell: 9, stacks: stacksFor(2, 0).slice(0, 5), special: { i: 6, j: 6, h: 1 }, glow: t.glow })}
    <circle cx="90" cy="59" r="24" fill="#ff3b3b" stroke="#fff" stroke-width="4"/><path d="M82 47 L102 59 L82 71Z" fill="#fff"/>
    <rect x="60" y="126" width="60" height="10" rx="5" fill="#fff"/></g>`,
  rules: (t) => `<g transform="translate(88 22)"><path d="M14 8h120c8 0 12 6 12 12v128c0 8-6 12-12 12H26c-8 0-12-6-12-12z" fill="#fff4d6" stroke="#7a4b00" stroke-width="4"/>
    <path d="M14 8c-12 0-14 20 0 20" fill="#f3d9a0" stroke="#7a4b00" stroke-width="4"/>
    ${[44, 62, 80, 98, 116, 134].map((y, k) => `<rect x="34" y="${y}" width="${k % 2 ? 70 : 92}" height="8" rx="4" fill="#c49a5a"/>`).join("")}
    <circle cx="132" cy="140" r="20" fill="#e23b3b" stroke="#fff" stroke-width="3"/>${star(132, 140, 10, "#ffd23f")}</g>`,
  guide: (t) => `<g transform="translate(160 100)"><circle r="70" fill="#0a0f24" stroke="#fff" stroke-width="6"/><circle r="56" fill="none" stroke="${t.glow}" stroke-width="3" stroke-dasharray="4 8"/>
    <path d="M0 -50 L14 0 L0 50 L-14 0Z" fill="#e23b3b" stroke="#fff" stroke-width="3"/><path d="M0 -50 L14 0 L-14 0Z" fill="#ff6b6b"/>
    <circle r="7" fill="#ffd23f"/><text y="-56" text-anchor="middle" font-size="14" font-weight="900" fill="#fff" dy="-8">N</text></g>`,
  book: (t) => `<g transform="translate(70 40)"><path d="M90 20 C60 4 24 4 4 12 v110 c20-8 56-8 86 8z" fill="#fff" stroke="#0a0f24" stroke-width="5"/>
    <path d="M90 20 C120 4 156 4 176 12 v110 c-20-8-56-8-86 8z" fill="#f1f4fb" stroke="#0a0f24" stroke-width="5"/>
    ${[34, 50, 66, 82, 98].map((y) => `<rect x="20" y="${y}" width="56" height="5" rx="2" fill="#9aa3b8"/><rect x="104" y="${y}" width="56" height="5" rx="2" fill="#9aa3b8"/>`).join("")}
    <path d="M80 -6 h20 v40 l-10 -8 l-10 8z" fill="#e23b3b"/></g>${cube(250, 120, 34, BLOCK.blue)}${cube(250, 98, 34, BLOCK.red)}`,
  help: (t) => `<g transform="translate(160 96)"><path d="M-70 -50 h140 a20 20 0 0 1 20 20 v62 a20 20 0 0 1 -20 20 h-70 l-34 28 v-28 h-36 a20 20 0 0 1 -20 -20 v-62 a20 20 0 0 1 20 -20z" fill="#fff" stroke="#0a0f24" stroke-width="5"/>
    <text y="34" text-anchor="middle" font-family="'Baloo 2','Arial Black',sans-serif" font-weight="900" font-size="84" fill="${t.a}">?</text></g>`,
  tournaments: (t) => `<g transform="translate(160 98)">
    <path d="M-44 -64 h88 v40 c0 36-22 56-44 56 s-44-20-44-56z" fill="#ffd23f" stroke="#7a4b00" stroke-width="5"/>
    <path d="M-44 -50 h-26 c0 30 12 42 30 44 M44 -50 h26 c0 30-12 42-30 44" fill="none" stroke="#ffd23f" stroke-width="9"/>
    <rect x="-10" y="30" width="20" height="18" fill="#d99a12"/><rect x="-38" y="46" width="76" height="20" rx="5" fill="#7a4b00"/>
    ${star(0, -26, 18, "#fff3b0")}</g>${sparkles(18, "#fff3b0", 4)}`,
  championship: (t) => `<ellipse cx="160" cy="176" rx="120" ry="16" fill="rgba(0,0,0,.35)"/>${podium(160, 176, 46)}`,
  news: (t) => `<g transform="translate(70 30) rotate(-4 90 70)"><rect width="180" height="130" rx="10" fill="#fff" stroke="#0a0f24" stroke-width="5"/>
    <rect x="14" y="14" width="152" height="22" rx="4" fill="#0a0f24"/><text x="90" y="31" text-anchor="middle" font-family="'Baloo 2','Arial Black',sans-serif" font-weight="900" font-size="17" fill="#ffd23f">XIMA NEWS</text>
    <rect x="14" y="46" width="70" height="56" rx="6" fill="${t.a}"/>${cube(49, 56, 28, BLOCK.gold)}
    ${[48, 60, 72, 84, 96].map((y) => `<rect x="94" y="${y}" width="72" height="6" rx="3" fill="#b8c0d4"/>`).join("")}
    <rect x="14" y="110" width="152" height="6" rx="3" fill="#b8c0d4"/></g>`,
  friends: (t) => `${person(64, 42, 92, BLOCK.blue[1])}${person(164, 42, 92, BLOCK.green[1])}<g transform="translate(160 54)"><path d="M0 14 C-22 -4 -12 -22 0 -10 C12 -22 22 -4 0 14z" fill="#ff4d6d" stroke="#fff" stroke-width="3"/></g>`,
  waiting: (t) => `${isoBoard({ cx: 160, cy: 46, cell: 15, stacks: [], special: { i: 6, j: 6, h: 0 }, glow: t.glow })}
    <g transform="translate(250 60)"><path d="M-24 -40 h48 M-24 40 h48" stroke="#fff" stroke-width="7" stroke-linecap="round"/>
    <path d="M-18 -36 c0 26 36 26 36 52 s-36 0-36 20 v0 h36 c0-20-36-26-36-52 s36-26 36-20z" fill="rgba(255,255,255,.18)" stroke="#fff" stroke-width="4"/>
    <path d="M-10 -24 h20 l-10 14z M-14 30 h28 l-14 -12z" fill="#ffd23f"/></g>
    ${person(36, 70, 64, BLOCK.blue[1])}<g transform="translate(68 52)"><circle r="14" fill="#0a0f24" stroke="#fff" stroke-width="3"/><text y="6" text-anchor="middle" font-weight="900" font-size="18" fill="#fff">?</text></g>`,
  search: (t) => `${isoBoard({ cx: 150, cy: 44, cell: 15, stacks: stacksFor(2, 0).slice(0, 6), special: { i: 6, j: 6, h: 1 }, glow: t.glow })}
    <g transform="translate(212 104)"><circle r="40" fill="rgba(255,255,255,.18)" stroke="#fff" stroke-width="10"/><path d="M28 28 L62 62" stroke="#fff" stroke-width="16" stroke-linecap="round"/><path d="M28 28 L62 62" stroke="#ffd23f" stroke-width="8" stroke-linecap="round"/></g>`,
  watch: (t) => `<g transform="translate(64 30)"><rect width="192" height="128" rx="16" fill="#0a0f24" stroke="#fff" stroke-width="5"/>
    <rect x="10" y="10" width="172" height="108" rx="10" fill="#101c44"/>
    ${isoBoard({ cx: 96, cy: 22, cell: 10, stacks: stacksFor(2, 0), special: { i: 6, j: 6, h: 2 }, glow: t.glow })}
    <g transform="translate(150 24)"><rect x="-26" y="-11" width="52" height="22" rx="11" fill="#e23b3b"/><circle cx="-14" cy="0" r="4" fill="#fff"/><text x="6" y="5" text-anchor="middle" font-weight="900" font-size="12" fill="#fff">LIVE</text></g>
    <path d="M70 128 l-14 22 M122 128 l14 22" stroke="#fff" stroke-width="6" stroke-linecap="round"/></g>`,
  design: (t) => `${isoBoard({ cx: 150, cy: 44, cell: 15, stacks: [{ i: 4, j: 4, colors: ["yellow"] }, { i: 8, j: 8, colors: ["green", "green"] }], special: { i: 6, j: 6, h: 0 }, glow: t.glow })}
    <g transform="translate(236 90) rotate(35)"><rect x="-8" y="-70" width="16" height="70" rx="6" fill="#c97a45" stroke="#fff" stroke-width="3"/><rect x="-10" y="-6" width="20" height="16" fill="#c3cad8"/><path d="M-10 10 h20 c0 20-6 30-10 34 c-4-4-10-14-10-34z" fill="#ff4d6d" stroke="#fff" stroke-width="3"/></g>
    <g transform="translate(58 130)"><ellipse rx="44" ry="30" fill="#fff4d6" stroke="#7a4b00" stroke-width="3"/><circle cx="-22" cy="-6" r="8" fill="#e23b3b"/><circle cx="0" cy="-14" r="8" fill="#2f63e8"/><circle cx="22" cy="-6" r="8" fill="#22b25a"/><circle cx="10" cy="12" r="8" fill="#f2bf1d"/></g>`,
  store: (t) => `<g transform="translate(96 40)"><path d="M10 40 h108 l-8 112 h-92z" fill="#ff4d6d" stroke="#fff" stroke-width="5" stroke-linejoin="round"/>
    <path d="M36 50 v-18 a28 28 0 0 1 56 0 v18" fill="none" stroke="#fff" stroke-width="7" stroke-linecap="round"/></g>
    ${cube(150, 108, 34, BLOCK.gold)}${cube(176, 116, 30, BLOCK.blue)}${star(232, 54, 16)}${sparkles(16, "#fff", 9)}`,
  premium: (t) => `<g transform="translate(160 100)"><path d="M-66 -24 L-36 -60 H36 L66 -24 L0 64Z" fill="#5ff3ff" stroke="#fff" stroke-width="5" stroke-linejoin="round"/>
    <path d="M-66 -24 H66 M-36 -60 L-18 -24 L0 64 M36 -60 L18 -24 L0 64 M-18 -24 L0 -60 L18 -24" fill="none" stroke="#0e7c86" stroke-width="3" stroke-linejoin="round"/>
    <path d="M-36 -60 L-18 -24 H18 L36 -60Z" fill="#b8fbff" opacity=".7"/></g>${sparkles(22, "#fff", 12)}`,
  settings: (t) => `<g transform="translate(140 104)">${gear(0, 0, 54, "#dbe4ff")}</g><g transform="translate(230 62)">${gear(0, 0, 28, "#ffd23f")}</g>`,
  support: (t) => `<g transform="translate(160 100)"><circle r="64" fill="#fff" stroke="#0a0f24" stroke-width="4"/><circle r="30" fill="${t.a}" stroke="#0a0f24" stroke-width="4"/>
    ${[0, 1, 2, 3].map((k) => `<path d="M${Math.cos((k * Math.PI) / 2 - 0.42) * 64} ${Math.sin((k * Math.PI) / 2 - 0.42) * 64} A64 64 0 0 1 ${Math.cos((k * Math.PI) / 2 + 0.42) * 64} ${Math.sin((k * Math.PI) / 2 + 0.42) * 64} L${Math.cos((k * Math.PI) / 2 + 0.6) * 30} ${Math.sin((k * Math.PI) / 2 + 0.6) * 30} A30 30 0 0 0 ${Math.cos((k * Math.PI) / 2 - 0.6) * 30} ${Math.sin((k * Math.PI) / 2 - 0.6) * 30}Z" fill="#e23b3b"/>`).join("")}</g>`,
  stats: (t) => {
    let out = `<line x1="60" y1="170" x2="270" y2="170" stroke="#fff" stroke-width="4" stroke-linecap="round"/>`;
    [[86, 1, "blue"], [134, 2, "green"], [182, 3, "yellow"], [230, 4, "red"]].forEach(([x, h, c]) => {
      for (let k = 0; k < h; k += 1) out += cube(x, 170 - 34 - k * 21, 34, BLOCK[c]);
    });
    return `${out}<path d="M70 120 L130 96 L180 104 L250 44" fill="none" stroke="#ffd23f" stroke-width="5" stroke-linecap="round" stroke-linejoin="round"/><path d="M238 42 L252 42 L250 56" fill="none" stroke="#ffd23f" stroke-width="5" stroke-linecap="round"/>`;
  },
  create: (t) => `${isoBoard({ cx: 150, cy: 44, cell: 15, stacks: [], special: { i: 6, j: 6, h: 0 }, glow: t.glow })}
    <g transform="translate(232 108)"><circle r="40" fill="#22c55e" stroke="#fff" stroke-width="5"/><path d="M0 -20 V20 M-20 0 H20" stroke="#fff" stroke-width="10" stroke-linecap="round"/></g>`,
  bot: (t) => `<g transform="translate(160 104)"><rect x="-58" y="-44" width="116" height="90" rx="24" fill="#dbe4ff" stroke="#fff" stroke-width="5"/>
    <rect x="-44" y="-28" width="88" height="44" rx="14" fill="#0a0f24"/><circle cx="-20" cy="-6" r="10" fill="#5ff3ff"/><circle cx="20" cy="-6" r="10" fill="#5ff3ff"/>
    <rect x="-22" y="24" width="44" height="8" rx="4" fill="#7c869c"/><path d="M0 -44 V-64" stroke="#fff" stroke-width="5"/><circle cy="-68" r="8" fill="#ff4d6d"/>
    <rect x="-72" y="-14" width="14" height="30" rx="6" fill="#93a6d8"/><rect x="58" y="-14" width="14" height="30" rx="6" fill="#93a6d8"/></g>
    ${cube(56, 132, 30, BLOCK.blue)}${cube(264, 132, 30, BLOCK.red)}`,
  ranking: (t) => `<ellipse cx="160" cy="176" rx="120" ry="16" fill="rgba(0,0,0,.35)"/>${podium(160, 176, 46)}`,
  history: (t) => `${miniBoardCard(150, 30, 120, "science", 8)}${miniBoardCard(120, 56, 130, "cienzar", -4)}${clock(72, 100, 48, "#ffd23f")}`,
  puzzles: (t) => `${isoBoard({ cx: 170, cy: 40, cell: 16, stacks: stacksFor(2, 0).slice(0, 6), special: { i: 6, j: 6, h: 1 }, glow: t.glow })}${puzzlePiece(22, 70, 74, "#a855f7")}${puzzlePiece(226, 30, 54, "#ffd23f")}`,
  learn: (t) => `${ICONS.book(t)}`,
  arena: (t) => `${isoBoard({ cx: 160, cy: 54, cell: 15, stacks: [{ i: 5, j: 5, colors: ["yellow", "green"] }, { i: 8, j: 7, colors: ["blue"] }], special: { i: 6, j: 6, h: 0 }, glow: t.glow })}${sparkles(20, "#fff", 2)}`,
};

function gear(cx, cy, r, color) {
  let d = "";
  const teeth = 10;
  for (let k = 0; k < teeth * 2; k += 1) {
    const a = (k * Math.PI) / teeth;
    const rr = k % 2 ? r * 0.82 : r;
    const a1 = a - Math.PI / teeth / 2;
    const a2 = a + Math.PI / teeth / 2;
    d += `${k ? "L" : "M"}${(cx + rr * Math.cos(a1)).toFixed(1)} ${(cy + rr * Math.sin(a1)).toFixed(1)} L${(cx + rr * Math.cos(a2)).toFixed(1)} ${(cy + rr * Math.sin(a2)).toFixed(1)} `;
  }
  return `<path d="${d}Z" fill="${color}" stroke="#0a0f24" stroke-width="3" stroke-linejoin="round"/><circle cx="${cx}" cy="${cy}" r="${r * 0.36}" fill="#0a0f24"/>`;
}

export const iconArt = (key, tone = "slate") => {
  const t = TONES[tone] || TONES.slate;
  const fn = ICONS[key];
  return wrap(`${backdrop(tone, "dots", key.length)}${fn ? fn(t) : ""}`);
};

// Fondo garabateado (como el de la galería de juegos) con bloques, dados, átomos y estrellas.
export const doodleBackground = () => {
  let out = "";
  let a = 7;
  const rnd = () => {
    a = (a * 9301 + 49297) % 233280;
    return a / 233280;
  };
  const W = 1400;
  const H = 300;
  for (let k = 0; k < 70; k += 1) {
    const x = rnd() * W;
    const y = rnd() * H;
    const r = rnd() * 360;
    const kind = Math.floor(rnd() * 5);
    const s = 18 + rnd() * 16;
    const st = `fill="none" stroke="#fff" stroke-opacity=".09" stroke-width="2"`;
    if (kind === 0) out += `<rect x="${x}" y="${y}" width="${s}" height="${s}" rx="4" transform="rotate(${r} ${x} ${y})" ${st}/><circle cx="${x + s / 2}" cy="${y + s / 2}" r="2" fill="#fff" fill-opacity=".1" transform="rotate(${r} ${x} ${y})"/>`;
    if (kind === 1) out += `<g transform="translate(${x} ${y}) rotate(${r})"><ellipse rx="${s / 1.4}" ry="${s / 4}" ${st}/><ellipse rx="${s / 1.4}" ry="${s / 4}" transform="rotate(60)" ${st}/><ellipse rx="${s / 1.4}" ry="${s / 4}" transform="rotate(-60)" ${st}/></g>`;
    if (kind === 2) out += `<g transform="translate(${x} ${y}) rotate(${r})"><path d="M0 ${-s / 2} L${s / 2} ${-s / 4} L0 0 L${-s / 2} ${-s / 4}Z M${-s / 2} ${-s / 4} V${s / 4} L0 ${s / 2} V0 M${s / 2} ${-s / 4} V${s / 4} L0 ${s / 2}" ${st}/></g>`;
    if (kind === 3) out += `<g transform="translate(${x} ${y}) rotate(${r})"><path d="M0 ${-s / 2} L${s * 0.12} ${-s * 0.12} L${s / 2} 0 L${s * 0.12} ${s * 0.12} L0 ${s / 2} L${-s * 0.12} ${s * 0.12} L${-s / 2} 0 L${-s * 0.12} ${-s * 0.12}Z" ${st}/></g>`;
    if (kind === 4) out += `<g transform="translate(${x} ${y}) rotate(${r})"><path d="M${-s / 2} ${-s / 3} h${s} v${s * 0.66} h${-s}z M${-s / 6} ${-s / 3} v${s * 0.66} M${s / 6} ${-s / 3} v${s * 0.66}" ${st}/></g>`;
  }
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${W} ${H}" preserveAspectRatio="xMidYMid slice">${out}</svg>`;
};

export const toDataUri = (svg) => `url("data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}")`;
